<script setup>
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import { useLoadsStore } from '@/stores/Loads'
import { useToastStore } from '@/stores/Toast'
import { useDriverStore } from '@/stores/Drivers'
import relativeTime from 'dayjs/plugin/relativeTime'
import CreateCommentForm from './CreateCommentForm.vue'
import LoadDrawerSegment from './LoadDrawerSegment.vue'
import LoadDrawerComments from './LoadDrawerComments.vue'
import LoadDrawerShipment from './LoadDrawerShipment.vue'
import { useLoadCommentsStore } from '@/stores/LoadComments'
import { useLoadSegmentsStore } from '@/stores/LoadSegments'
import { useLoadShipmentsStore } from '@/stores/LoadShipments'
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue'
import {
    DELETE_LOAD,
    IN_TRANSIT,
    SEND_ELD,
    UPDATE_TRUCK,
    UPDATE_DRIVER,
    UPDATE_STATUS,
    VIEW_COMMENTS,
    VIEW_LOAD,
    UPDATE_TRUCK_LOCATION,
} from '@/constants/dispatch'

/**
 * The dayjs plugin.
 */
dayjs.extend(relativeTime)

/**
 * Constant representing the driver info action.
 *
 * @type {String}
 */
const DRIVER_INFO = 'driver-info'

/**
 * Constant representing the print load action.
 *
 * @type {String}
 */
const PRINT_LOAD = 'print-load'

/**
 * Store instances.
 */
const uiStore = useUiStore()
const commentStore = useLoadCommentsStore()
const driverStore = useDriverStore()
const segmentStore = useLoadSegmentsStore()
const shipmentStore = useLoadShipmentsStore()

/**
 * Composable instances.
 */
const toastStore = useToastStore()

/**
 * Local refs.
 */
const show = ref(false)
const isLoading = ref(false)
const openSegmentPanel = ref(null)
const actions = ref({
    DELETE_LOAD,
    SEND_ELD,
    UPDATE_TRUCK,
    UPDATE_DRIVER,
    UPDATE_STATUS,
    VIEW_COMMENTS,
    VIEW_LOAD,
    UPDATE_TRUCK_LOCATION,
})

/**
 * Get the active tab.
 *
 * @return {String}
 */
const activeTab = computed({
    get() {
        return uiStore.activeLoadDrawerTab
    },
    set(value) {
        uiStore.setLoadDrawerTab(value)
    },
})
/**
 * The load's current segment.
 *
 * @return {Object}
 */
const currentSegment = computed(() => {
    return segmentStore.find(load.value.current_load_segment_id) || {}
})

/**
 * The load distance object.
 *
 * @return {Object}
 */
const distances = computed(() => {
    if (load.value) {
        return load.value.summary?.distances || {}
    }

    return {}
})

/**
 * The current driver from the load's segment.
 *
 * @return {Object}
 */
const driver = computed(() => {
    if (hasDriver.value) {
        return currentSegment.value.drivers[0]
    }

    return {}
})

/**
 * Calculates the estimated revenue.
 *
 * @return {Number}
 */
const estimatedRevenue = computed(() => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    })

    const revenue = load.value.summary?.revenue
    if (!revenue) {
        return 0
    }

    const total =
        revenue.reduce((total, current) => {
            const keys = Object.keys(current)

            keys.forEach((key) => {
                if (key !== 'type') {
                    total += current[key]
                }
            })

            return total
        }, 0) || 0

    return formatter.format(total)
})

/**
 * Determine if the load has a driver assigned.
 *
 * @return {Boolean}
 */
const hasDriver = computed(() => {
    return (
        !!(
            currentSegment.value && (currentSegment.value.drivers || []).length
        ) && !hasCarrier.value
    )
})

/**
 * Determine if the load has a carrier assigned.
 *
 * @return {Boolean}
 */
const hasCarrier = computed(() => {
    return !!load.value.carrier
})

/**
 * Determine if the driver has hours of service.
 *
 * @return {Boolean}
 */
const hasHos = computed(() => {
    if (!hasDriver.value) {
        return false
    }

    return !!driver.value.driver.hours_of_service?.updated_at
})

/**
 * Get the driver's hours of service object.
 *
 * @return {Object}
 */
const hoursOfService = computed(() => {
    const defaultHos = {
        updated_at: null,
        on_duty_remaining: 0,
        drive_remaining: 0,
    }

    if (!hasHos.value) {
        return defaultHos
    }

    return driver.value.driver.hours_of_service
})

/**
 * The hours of service updated at timestamp.
 *
 * @return {String}
 */
const hosUpdatedAt = computed(() => {
    if (!hoursOfService.value.updated_at) {
        return
    }

    return dayjs().from(dayjs(hoursOfService.value.updated_at), 'day')
})

/**
 * The active load.
 *
 * @return {Object}
 */
const load = computed(() => {
    return uiStore.actionLoads(VIEW_LOAD)
})

/**
 * The on duty remaining time.
 *
 * @return {String}
 */
const onDutyRemainingTime = computed(() => {
    if (!hoursOfService.value.on_duty_remaining) {
        return
    }

    const dutyRemaining = Math.abs(hoursOfService.value.on_duty_remaining)

    const hours = Math.floor(dutyRemaining / 60)
    const remainingMinutes = dutyRemaining % 60
    const prefix = hoursOfService.value.on_duty_remaining < 0 ? '-' : ''

    const time = dayjs()
        .startOf('day')
        .add(hours, 'hour')
        .add(remainingMinutes, 'minute')
        .format('H:mm')

    return `${prefix}${time}`
})

/**
 * The drive remaining time.
 *
 * @return {String}
 */
const driveTimeRemaining = computed(() => {
    if (!hoursOfService.value.drive_remaining) {
        return ''
    }

    const driveRemaining = Math.abs(hoursOfService.value.drive_remaining)
    const hours = Math.floor(driveRemaining / 60)
    const remainingMinutes = driveRemaining % 60
    const prefix = hoursOfService.value.drive_remaining < 0 ? '-' : ''

    const time = dayjs()
        .startOf('day')
        .add(hours, 'hour')
        .add(remainingMinutes, 'minute')
        .format('H:mm')

    return `${prefix}${time}`
})

/**
 * Determine if the load is pending or delivered.
 *
 * @return {Boolean}
 */
const isInTransit = computed(() => {
    return load.value.status.code === IN_TRANSIT
})

/**
 * Determine if the truck and carrier are empty.
 *
 * @return {Boolean}
 */
const isTruckAndCarrierEmpty = computed(() => {
    return !!(!load.value.truck?.id && !hasCarrier.value)
})

/**
 * The load's segments.
 */
const segments = computed(() => {
    if (load.value) {
        return segmentStore.filterByLoadId(load.value.id)
    }

    return []
})

/**
 * The load's shipments.
 */
const shipments = computed(() => {
    if (load.value) {
        return shipmentStore.filterByLoadId(load.value.id)
    }

    return []
})

/**
 * The load's status.
 *
 * @return {String}
 */
const status = computed(() => {
    if (load.value) {
        return load.value.status.name
    }

    return ''
})

/**
 * Fetch the load comments.
 *
 * @return {void}
 */
const fetchComments = async () => {
    try {
        isLoading.value = true
        await commentStore.get(load.value.id)
    } catch (error) {
        console.log(error)
    } finally {
        isLoading.value = false
    }
}

/**
 * Fetch the segments.
 *
 * @return {void}
 */
const fetchSegments = async () => {
    try {
        isLoading.value = true
        await segmentStore.get(load.value.id)

        openSegmentPanel.value = load.value.current_load_segment_id
    } catch (error) {
        console.log(error)
    } finally {
        isLoading.value = false
    }
}

/**
 * Fetch the shipments.
 *
 * @return {void}
 */
const fetchShipments = async () => {
    try {
        isLoading.value = true
        await shipmentStore.get(load.value.id)
    } catch (error) {
        console.log(error)
    } finally {
        isLoading.value = false
    }
}

/**
 * Open the given action popup.
 *
 * @param {String} action
 * @return {void}
 */
const onActionClick = (action) => {
    if (action === DRIVER_INFO) {
        return driverAction()
    }

    if (action === PRINT_LOAD) {
        return printLoadAction()
    }

    uiStore.openAction(action, { id: load.value.id })
}

/**
 * Handle the driver action.
 *
 * @return {void}
 */
const driverAction = () => {
    if (!load.value?.drivers?.length) {
        return toastStore.setNotification({
            message: 'No driver found',
            type: 'error',
        })
    }

    const id = load.value.drivers[0].id

    driverStore.setActive(id)
}

/**
 * Fetch the load's data.
 *
 * @return {void}
 */
const fetchData = async () => {
    if (activeTab.value === 'stops') {
        await fetchSegments()
    }

    if (activeTab.value === 'shipments') {
        await fetchShipments()
    }

    if (activeTab.value === 'comments') {
        await fetchComments()
    }
}

/**
 * Handle the close event.
 *
 * @return {void}
 */
const onClose = () => {
    show.value = false
}

/**
 * Handle the tab change event.
 *
 * @param {String} tab
 */
const onChangeTab = (tab) => {
    activeTab.value = tab
}

/**
 * Handle the open load click event.
 *
 * @return {void}
 */
const onOpenLoad = () => {
    const url = `/index.cfm?module=dispatch&template=dspLoad&intLoadId=${load.value.id}`

    location.href = url
}

/**
 * Print the load sheet.
 *
 * @return {void}
 */
const printLoadAction = async () => {
    await segmentStore.get(load.value.id)

    const segmentId = load.value.current_load_segment_id
    const toastStore = useToastStore()

    if (segmentId) {
        try {
            toastStore.setNotification({
                isLoading: true,
                message: `Downloading Load Sheet`,
                type: 'info',
            })

            const store = useLoadsStore()

            const url = await store.printLoad(segmentId)

            toastStore.setNotification({
                message: `Load sheet printed successfully`,
                type: 'success',
            })

            window.open(url, '_blank', 'noreferrer')
        } catch (error) {
            toastStore.setNotification({
                message: error.message(),
                type: 'error',
            })
        }
    }
}

/**
 * Watch for the active state.
 */
watch(
    load,
    async (value) => {
        setTimeout(() => (show.value = !!value), 100)

        if (!value) {
            return
        }

        fetchData()
    },
    { immediate: true }
)

watch(activeTab, async () => {
    fetchData()
})

/**
 * Watch for the show state.
 */
watch(show, (value) => {
    if (!value) {
        uiStore.setLoadDrawerTab(null)

        return setTimeout(() => uiStore.closeAction(VIEW_LOAD), 300)
    }
})
</script>

<template>
    <v-navigation-drawer
        :scrim="false"
        temporary
        location="right"
        width="600"
        v-model="show"
    >
        <div class="flex flex-col h-full">
            <div class="flex items-center p-3 border-b border-edge z-50">
                <button class="mr-2" @click.prevent="onClose">
                    <v-icon icon="fas fa-arrow-left" />
                </button>
                <div class="flex items-center justify-between px-3 w-full">
                    <h2 class="font-semibold">Load #{{ load.id }}</h2>
                    <div class="flex items-center">
                        <v-btn
                            variant="outlined"
                            class="border border-edge mr-3"
                            @click.prevent="onOpenLoad"
                        >
                            Open
                        </v-btn>
                        <v-menu>
                            <template v-slot:activator="{ props }">
                                <v-btn
                                    append-icon="fal fa-chevron-down"
                                    elevation="0"
                                    variant="outlined"
                                    class="border border-edge"
                                    v-bind="props"
                                >
                                    Actions
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item
                                    @click.prevent="
                                        onActionClick(actions.UPDATE_DRIVER)
                                    "
                                >
                                    Change Current Driver
                                </v-list-item>
                                <v-list-item
                                    @click.prevent="
                                        onActionClick(actions.UPDATE_STATUS)
                                    "
                                >
                                    Change Status
                                </v-list-item>
                                <v-list-item
                                    @click.prevent="
                                        onActionClick(actions.UPDATE_TRUCK)
                                    "
                                >
                                    Change Current Truck
                                </v-list-item>
                                <v-list-item
                                    @click.prevent="
                                        onActionClick(actions.DELETE_LOAD)
                                    "
                                    :disabled="isInTransit"
                                >
                                    Delete Load
                                </v-list-item>
                                <v-list-item
                                    @click.prevent="onActionClick(DRIVER_INFO)"
                                >
                                    Driver Info
                                </v-list-item>
                                <v-list-item
                                    :disabled="isTruckAndCarrierEmpty"
                                    @click.prevent="
                                        onActionClick(actions.SEND_ELD)
                                    "
                                >
                                    Send Load to ELD
                                </v-list-item>
                                <v-list-item
                                    :disabled="isTruckAndCarrierEmpty"
                                    @click.prevent="
                                        onActionClick(
                                            actions.UPDATE_TRUCK_LOCATION
                                        )
                                    "
                                >
                                    Set Truck Location
                                </v-list-item>
                                <v-list-item
                                    @click.prevent="onActionClick(PRINT_LOAD)"
                                >
                                    Print Load
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </div>
            </div>

            <v-divider></v-divider>

            <div class="h-full overflow-auto py-6">
                <div class="flex flex-col w-full h-full">
                    <div class="flex flex-col mb-6">
                        <div class="flex px-6 w-full">
                            <div
                                class="flex flex-nowrap border border-gray-300 w-full"
                            >
                                <div
                                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-1/3"
                                >
                                    <span class="text-sm mb-2 text-gray-500">
                                        Status
                                    </span>
                                    <div class="flex items-center mb-2">
                                        <v-icon
                                            class="mr-2 text-green-600 text-xs"
                                            icon="fas fa-circle"
                                        ></v-icon>

                                        <button
                                            @click.prevent="
                                                onActionClick(
                                                    actions.UPDATE_STATUS
                                                )
                                            "
                                            class="text-sm font-semibold mr-2 underline cursor-pointer"
                                        >
                                            {{ status }}
                                        </button>
                                    </div>
                                    <span class="text-xs mt-auto text-gray-500">
                                        {{ load.summary?.stops?.arrived || 0 }}
                                        of
                                        {{ load.summary?.stops?.total || 0 }}
                                        stops arrived
                                    </span>
                                </div>

                                <div
                                    class="flex flex-col border-r border-r-gray-300 p-2 h-full w-1/3"
                                >
                                    <span class="text-sm mb-2 text-gray-500">
                                        Category
                                    </span>

                                    <div class="flex items-center">
                                        <span
                                            class="text-sm font-semibold mr-2"
                                        >
                                            {{ load.category?.name }}
                                        </span>
                                    </div>
                                </div>

                                <div class="flex flex-col p-2 h-full w-1/3">
                                    <span class="text-sm mb-2 text-gray-500">
                                        Current Driver
                                    </span>

                                    <div class="flex items-center mb-2">
                                        <span
                                            class="text-sm font-semibold mr-2"
                                            v-if="hasDriver"
                                        >
                                            {{ driver.last_name }},
                                            {{ driver.first_name }}
                                        </span>

                                        <span
                                            class="text-sm font-semibold mr-2"
                                            v-else
                                        >
                                            -
                                        </span>
                                    </div>
                                    <div
                                        v-if="hasHos"
                                        class="flex flex-wrap text-xs text-gray-500 whitespace-break-spaces"
                                    >
                                        <span>
                                            HOS: {{ driveTimeRemaining }} /
                                            {{ onDutyRemainingTime }}
                                        </span>

                                        <span> - {{ hosUpdatedAt }} ago</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col px-6 mb-6">
                        <div
                            class="flex flex-nowrap border border-gray-300 w-full"
                        >
                            <div
                                class="flex border-r border-r-gray-300 p-2 h-full w-1/2"
                            >
                                <div class="flex flex-col w-1/2 justify-center">
                                    <span class="text-sm mb-2 text-gray-500">
                                        Loaded Miles
                                    </span>
                                    <span class="text-base">
                                        {{ (distances.loaded || 0).toFixed() }}
                                    </span>
                                </div>

                                <div class="flex flex-col w-1/2 justify-center">
                                    <span class="text-sm mb-2 text-gray-500">
                                        Total Miles
                                    </span>
                                    <span class="text-base">{{
                                        (
                                            distances.loaded +
                                                distances.empty || 0
                                        ).toFixed()
                                    }}</span>
                                </div>
                            </div>

                            <div class="flex p-2 h-full w-1/2">
                                <div class="flex flex-col w-1/2 justify-center">
                                    <span class="text-sm mb-2 text-gray-500">
                                        # of Shipments
                                    </span>
                                    <span class="text-base">{{
                                        load.summary.shipments || '-'
                                    }}</span>
                                </div>

                                <div class="flex flex-col w-1/2 justify-center">
                                    <span class="text-sm mb-2 text-gray-500">
                                        Est. Revenue
                                    </span>

                                    <span class="text-base">
                                        {{ estimatedRevenue }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="border-b border-b-gray-300">
                        <v-tabs
                            color="secondary"
                            fixed-tabs
                            :model-value="activeTab"
                            @update:model-value="onChangeTab"
                        >
                            <v-tab value="stops"> Stops </v-tab>
                            <v-tab value="shipments"> Shipments </v-tab>
                            <v-tab value="comments"> Comments </v-tab>
                        </v-tabs>
                    </div>

                    <div v-if="activeTab !== 'comments'" class="p-6">
                        <loading-overlay v-if="isLoading" />
                        <v-expansion-panels
                            class="h-full !overflow-auto"
                            v-if="activeTab === 'stops' && !isLoading"
                            v-model="openSegmentPanel"
                        >
                            <load-drawer-segment
                                v-for="(segment, index) in segments"
                                :key="index"
                                :segment="segment"
                                :load="load"
                                :currentStopId="load.current_stop.id"
                                :currentLocation="load.current_location"
                                :index="index"
                                @change:driver="
                                    onActionClick(actions.UPDATE_DRIVER)
                                "
                                @change:truck="
                                    onActionClick(actions.UPDATE_TRUCK)
                                "
                            />
                        </v-expansion-panels>
                        <v-expansion-panels
                            class="h-full !overflow-auto"
                            v-if="activeTab === 'shipments' && !isLoading"
                        >
                            <load-drawer-shipment
                                v-for="(shipment, index) in shipments"
                                :key="index"
                                :shipment="shipment"
                            />
                        </v-expansion-panels>
                    </div>
                    <div
                        v-else-if="activeTab === 'comments'"
                        class="flex flex-grow w-full justify-center"
                    >
                        <loading-overlay v-if="isLoading" class="p-6" />
                        <load-drawer-comments v-else :load-id="load.id" />
                    </div>
                </div>
            </div>
            <create-comment-form
                v-if="activeTab === 'comments'"
                :loadId="load.id"
            />
        </div>
    </v-navigation-drawer>
</template>
